import {inject, Injectable} from "@angular/core";
import {map, Observable} from "rxjs";
import {Deal} from "../models/deal";
import {DealWithSeller} from "../models/deal-with-seller";
import {DealFund} from "../models/deal-fund";
import {DealScenario} from "../models/deal-scenario";
import {AggregatedDealCashflows} from "../models/aggregated-deal-cashflows";
import {DealReturnSummary} from "../models/deal-return-summary";
import {DealCompany} from "../models/deal-company";
import {DealApiService, GoalSeekRequest, PreviewDealReturnSummaryRequest} from "../generated-sources/api/deal";
import {
    AssetCurrencyAssignment,
    AssetFxRateAssignment,
    DealFxAnalysis,
    FundCurrencyAssignment
} from "../models/deal-fx-analysis";
import {DealData} from "../models/deal-data";
import {SellerPosition} from "../models/seller-position";
import {DealWeightedCashflowBreakdown} from "../models/deal-weighted-cashflow-breakdown";
import {DealPage} from "../models/deal-page";
import {DealOverviewParams} from "../store/deal.state";

@Injectable({
    providedIn: "root"
})
export class DealService {

    private readonly dealApiService = inject(DealApiService);

    public searchDeals(params: DealOverviewParams): Observable<DealPage> {
        return this.dealApiService.searchDeals(
            params.search || undefined,
            params.page,
            params.size,
            params.sort,
            params.archived
        );
    }

    public archiveDeal(dealId: number, archive: boolean): Observable<DealWithSeller> {
        return this.dealApiService.patchDeal(dealId, {archive});
    }

    public getDeals(): Observable<Deal[]> {
        return this.dealApiService.getDeals();
    }

    public getDealsForSeller(sellerId: number): Observable<Deal[]> {
        return this.dealApiService.getDealsForSeller(sellerId);
    }

    public getDealData(dealId: number): Observable<DealData> {
        return this.dealApiService.getDealData(dealId);
    }

    public getDeal(dealId: number): Observable<DealWithSeller> {
        return this.dealApiService.getDeal(dealId);
    }

    public update(dealId: number, deal: Deal, sellerId?: number): Observable<DealData> {
        return this.dealApiService.updateDeal(dealId, deal, sellerId);
    }

    public create(deal: Deal, sellerId?: number): Observable<Deal> {
        return this.dealApiService.createDeal(deal, sellerId);
    }

    public copyDeal(dealId: number, closingDate: string, projectName: string): Observable<Deal> {
        return this.dealApiService.copyDeal(dealId, {closingDate, projectName});
    }

    public updateDealName(dealId: number, name: string): Observable<DealWithSeller> {
        return this.dealApiService.patchDeal(dealId, {name});
    }

    public updateSellerId(dealId: number, sellerId: number): Observable<DealWithSeller> {
        return this.dealApiService.patchDeal(dealId, {sellerId})
    }

    public deleteDeal(dealId: number): Observable<any> {
        return this.dealApiService.deleteDeal(dealId)
    }

    public getDealFunds(dealId: number): Observable<DealFund> {
        return this.dealApiService.getDealFunds(dealId);
    }

    public updateDealFunds(dealId: number, sellerPositions: SellerPosition[], bidPriceAdjustment?: number, goalSeekSettings?: {
        goalSeekTvpi?: number;
        goalSeekTvpiActive?: boolean;
        goalSeekIrr?: number;
        goalSeekIrrActive?: boolean;
        goalSeekDiscount?: number;
        goalSeekDiscountActive?: boolean;
    }): Observable<DealData> {
        return this.dealApiService.updateDealFunds(dealId, {
            sellerPositions,
            bidPriceAdjustment,
            ...goalSeekSettings
        })
    }

    public getDealCompanies(dealId: number): Observable<DealCompany> {
        return this.dealApiService.getDealCompanies(dealId);
    }

    public getDealFxAnalysis(dealId: number): Observable<DealFxAnalysis> {
        return this.dealApiService.getDealFxAnalysis(dealId);
    }

    public updateAssetFxRates(dealId: number, updates: AssetFxRateAssignment[]): Observable<DealFxAnalysis> {
        return this.dealApiService.updateAssetFxRates(dealId, {updates});
    }

    public setDealCurrencies(
        dealId: number,
        fundCurrencies: FundCurrencyAssignment[],
        assetCurrencies: AssetCurrencyAssignment[]
    ): Observable<DealFxAnalysis> {
        return this.dealApiService.setDealCurrencies(dealId, {fundCurrencies, assetCurrencies});
    }

    public getScenariosForDeal(dealId: number): Observable<DealScenario[]> {
        return this.dealApiService.getScenariosForDeal(dealId);
    }

    public getDealScenario(dealId: number, scenarioId: number): Observable<DealScenario> {
        return this.dealApiService.getDealScenario(dealId, scenarioId);
    }

    public saveScenario(dealId: number, scenario: DealScenario): Observable<DealScenario> {
        const result: Observable<DealScenario> = !!scenario.id && !!dealId
            ? this.dealApiService.updateDealScenario(dealId, scenario.id, scenario)
            : this.dealApiService.createDealScenario(dealId, scenario);
        return result.pipe(map((scenarioDto: DealScenario) => scenarioDto));
    }

    public saveDealScenarios(dealId: number, scenarios: DealScenario[]): Observable<DealScenario[]> {
        return this.dealApiService.updateDealScenarios(dealId, scenarios);
    }

    public getAggregatedDealCashflows(dealId: number, scenarioId: number): Observable<AggregatedDealCashflows> {
        return this.dealApiService.getAggregatedDealCashflows(dealId, scenarioId);
    }

    public calcDealReturnSummary(dealId: number): Observable<DealReturnSummary> {
        return this.dealApiService.calculateDealReturnSummary(dealId);
    }

    public goalSeekDeal(dealId: number, request: GoalSeekRequest): Observable<DealReturnSummary> {
        return this.dealApiService.goalSeekDeal(dealId, request);
    }

    public previewDealReturnSummary(dealId: number, request: PreviewDealReturnSummaryRequest): Observable<DealReturnSummary> {
        return this.dealApiService.previewDealReturnSummary(dealId, request);
    }

    public getDealWeightedCashflowBreakdown(
        dealId: number, date: string, cashflowTypes: string[]
    ): Observable<DealWeightedCashflowBreakdown> {
        return this.dealApiService.getDealWeightedCashflowBreakdown(dealId, date, cashflowTypes);
    }
}
